import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener } from '@angular/core';
import { IframeResizeService } from 'src/app/core/services/iframe-resize.service';

const IFRAME_SIZE_OPTIONS = [
  {
    label: 'P',
    description: 'Pequeno',
    widthPx: 400,
  },
  {
    label: 'M',
    description: 'Medio',
    widthPx: 560,
  },
  {
    label: 'G',
    description: 'Grande',
    widthPx: 720,
  },
  {
    label: 'GG',
    description: 'Maximo',
    widthPx: 880,
  },
] as const;

const DEFAULT_IFRAME_SIZE_INDEX = 0;

@Component({
  selector: 'app-resize-buttons',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './resize-buttons.component.html',
  styleUrl: './resize-buttons.component.css',
})
export class ResizeButtonsComponent {
  readonly sizeOptions = IFRAME_SIZE_OPTIONS;
  readonly minIndex = 0;
  readonly maxIndex = IFRAME_SIZE_OPTIONS.length - 1;

  selectedIndex = DEFAULT_IFRAME_SIZE_INDEX;
  currentWidthPx = 0;
  isButtonsPanelOpen = false;

  constructor(
    private iframeResizeService: IframeResizeService,
    private elementRef: ElementRef<HTMLElement>,
  ) {
    const serviceWidth = this.iframeResizeService.getCurrentWidth();
    this.selectedIndex = this.getClosestIndexByWidth(serviceWidth);
    this.currentWidthPx = this.selectedOption.widthPx;
  }

  get selectedOption() {
    return this.sizeOptions[this.selectedIndex] ?? this.sizeOptions[0];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (
      this.isButtonsPanelOpen &&
      target &&
      !this.elementRef.nativeElement.contains(target)
    ) {
      this.isButtonsPanelOpen = false;
    }
  }

  onToggleButtonsPanel(): void {
    this.isButtonsPanelOpen = !this.isButtonsPanelOpen;
  }

  onSelectIndex(index: number): void {
    const clampedIndex = Math.min(
      this.maxIndex,
      Math.max(this.minIndex, index),
    );
    this.selectedIndex = clampedIndex;
    this.applySizeByIndex(clampedIndex);
  }

  resetToDefault(): void {
    this.selectedIndex = DEFAULT_IFRAME_SIZE_INDEX;
    this.applySizeByIndex(this.selectedIndex);
  }

  private applySizeByIndex(index: number): void {
    const option = this.sizeOptions[index];
    if (!option) {
      return;
    }

    const widthPx = option.widthPx;
    if (widthPx === this.currentWidthPx) {
      return;
    }

    if (this.iframeResizeService.setWidth(widthPx)) {
      this.currentWidthPx = widthPx;
    }
  }

  private getClosestIndexByWidth(widthPx: number): number {
    if (!Number.isFinite(widthPx)) {
      return DEFAULT_IFRAME_SIZE_INDEX;
    }

    let closestIndex = DEFAULT_IFRAME_SIZE_INDEX;
    let smallestDiff = Number.POSITIVE_INFINITY;

    for (let index = 0; index < this.sizeOptions.length; index += 1) {
      const optionWidthPx = this.sizeOptions[index].widthPx;
      const diff = Math.abs(optionWidthPx - widthPx);
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestIndex = index;
      }
    }

    return closestIndex;
  }
}
